import { useEffect, useState } from "react";
import { Button, Notification } from "@weirgroup/weir-dls";
import { useTranslation } from "react-i18next";

import "./styles.scss";
import { clearShoppingList, deleteShoppingListItem, getShoppingList } from "../../api/shopping-list.api";
import { ConfirmDialog } from "../../components/confirm-dialog/confirm-dialog";
import { ShoppingListItem, ShoppingListType } from "../../types/shopping-list.type";
import { ExportModal } from "./components/export-modal";
import { QuantityModal } from "./components/quantity-modal";
import { StorageService, ROLES } from "../../services/storage_service";
import { RolesType } from "../../types/user.type";
import { Roles } from "../../constants/roles.constants";
import { useDarkMode } from "../../hooks/use-darkmode";
import { useAnalyticsEvent } from "../../hooks/useAnalyticsEvent";
import { LoadingSpinner } from "../../components/loading-spinner";
import { Card } from "../../components/Card/card";

interface ConfirmDataType {
    confirmHandler: any;
    message: string;
}

export const ShoppingListPage = () => {
    const { t } = useTranslation();
    const logAnalyticsEvent = useAnalyticsEvent();
    const { darkMode } = useDarkMode();

    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
    const [showToast, setShowToast] = useState<boolean>(false);
    const [toastData, setToastData] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [shoppingList, setShoppingList] = useState<ShoppingListType>();
    const [confirmData, setConfirmData] = useState<ConfirmDataType>();
    const [isExportModalShown, setIsExportModalShown] = useState<boolean>(false);
    const [isQuantityModalShown, setIsQuantityModalShown] = useState<boolean>(false);
    const [selectedItem, setSelectedItem] = useState<ShoppingListItem | null>(null);
    const [isGuest, setIsGuest] = useState<boolean>(false);

    useEffect(() => {
        const roleData = StorageService.getItem(ROLES);
        if (roleData) {
            const roles: RolesType = JSON.parse(roleData);
            setIsGuest(roles.role === Roles.GUEST);
        }
        logAnalyticsEvent('web_user_viewed_shopping_list_page', { description: 'User viewed shopping list page' });
    }, []);

    useEffect(() => {
        fetchShoppingList();
    }, []);

    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => setShowToast(false), 5000);
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    const fetchShoppingList = () => {
        setIsLoading(true);
        getShoppingList()
            .then((list: any) => {
                setShoppingList(list.data.data);
                setIsLoading(false);
            })
            .catch((err: any) => {
                setIsLoading(false);
                console.error(err);
            });
    };

    const setConfirmDialogData = (isClearList: boolean, item_id?: number) => {
        let confirmHandler;

        if (isClearList) {
            confirmHandler = clearList;
        } else if (item_id === undefined) {
            confirmHandler = undefined;
        } else {
            confirmHandler = () => deleteItem(item_id);
        }
    
        setConfirmData({
            confirmHandler,
            message: isClearList ? t("clearListConfirmMessage") : t("deleteItemMessage"),
        });
    
        setIsConfirmOpen(true);
    };

    const clearList = () => {
        clearShoppingList()
            .then(() => {
                showToastMessage("success", t("clearListSuccessMessage"));
                fetchShoppingList();
                logAnalyticsEvent('web_user_cleared_shopping_list', { description: 'User cleared the shopping list' });
            })
            .catch((err: any) => {
                showToastMessage("critical", err.response.data.message);
            })
            .finally(() => setIsConfirmOpen(false));
    };

    const deleteItem = (item_id: number) => {
        setIsLoading(true);
        setIsConfirmOpen(false);
        deleteShoppingListItem(item_id)
            .then(() => {
                showToastMessage("success", t("deleteItemSuccessMessage"));
                fetchShoppingList();
            })
            .catch((err: any) => {
                showToastMessage("critical", err.response.data.message);
            })
            .finally(() => {
                setIsLoading(false);
                setIsConfirmOpen(false);
                logAnalyticsEvent('web_user_deleted_shopping_list_item', { description: 'User deleted a shopping list item' });
            });
    };

    const showToastMessage = (variant: "success" | "critical", message: string) => {
        setToastData({ variant, message });
        setShowToast(true);
    };

    const renderNoItems = () => (
        <div className="no-items">
            <p style={{color:darkMode ? 'white' : 'black'}}>{t("emptyShoppingList")}</p>
            <Button
                label={t("refresh")}
                size={"sm"}
                variant={"primary"}
                onClick={fetchShoppingList}
            />
        </div>
    );

    const renderShoppingList = () => (
        <>
            <div className="list-header" style={{ backgroundColor: darkMode ? "rgb(59, 67, 76)" : "white" }}>
                <p className="items" style={{ color: darkMode ? "white" : "black" }}>
                    {`Your List: ${shoppingList?.items.length} items`}
                </p>
                <div className="action-btns">
                    <Button
                        darkMode={darkMode}
                        label={t("clearList")}
                        size={"sm"}
                        variant={"secondary"}
                        onClick={() => setConfirmDialogData(true)}
                        disabled={isGuest}
                    />
                    <Button
                        darkMode={darkMode}
                        label={t("export")}
                        size={"sm"}
                        variant={"primary"}
                        onClick={() => setIsExportModalShown(true)}
                        disabled={isGuest}
                    />
                </div>
            </div>
            <div className={`shopping-list-body ${(shoppingList?.items?.length ?? 0) >= 2 ? "two-columns" : ""}`}>
                {shoppingList?.items.map((data: ShoppingListItem) => (
                    <Card
                        type="shopping-list"
                        shoppingListData={data}
                        key={data.shopping_cart_item_id}
                        onShoppingListDelete={(itemId: number) => setConfirmDialogData(false, itemId)}
                        onShoppingListEdit={(item: ShoppingListItem) => {
                            setSelectedItem(item);
                            setIsQuantityModalShown(true);
                        }}
                        isGuest={isGuest}
                        darkMode={darkMode}
                    />
                ))}
            </div>
        </>
    );
    const renderContent = () => {
        if ((shoppingList?.items?.length ?? 0) > 0) {
            return renderShoppingList();
        }
    
        if (isLoading) {
            return null;
        }
    
        return renderNoItems();
    };
    return (
        <div className="shopping-list-container" style={{ backgroundColor: darkMode ? "rgb(59, 67, 76)" : "white", overflowY: "auto" }}>
            {isConfirmOpen && (
                <ConfirmDialog
                    darkMode={darkMode}
                    isConfirmOpen={isConfirmOpen}
                    confirmHandler={confirmData?.confirmHandler}
                    cancelHandler={() => setIsConfirmOpen(false)}
                    confirmMessage={confirmData?.message}
                />
            )}
            {renderContent()}
            {showToast && (
                <div className="toast-container">
                    <Notification
                        title={toastData.variant === "success" ? "Success" : "Fail"}
                        textContent={toastData.message}
                        variant={toastData.variant}
                        onClose={() => setShowToast(false)}
                    />
                </div>
            )}
            {isLoading && (
                <LoadingSpinner darkMode={darkMode} />
            )}
            {isExportModalShown && (
                <ExportModal
                    darkMode={darkMode}
                    isModalShown={isExportModalShown}
                    setIsModalShown={setIsExportModalShown}
                    setToastData={setToastData}
                    setShowToast={setShowToast}
                />
            )}
            {isQuantityModalShown && (
                <QuantityModal
                    darkMode={darkMode}
                    shoppingListItem={selectedItem}
                    isModalShown={isQuantityModalShown}
                    setIsModalShown={setIsQuantityModalShown}
                    setToastData={setToastData}
                    setShowToast={setShowToast}
                    fetchShoppingList={fetchShoppingList}
                />
            )}
        </div>
    );
};